import { FileText, ExternalLink } from "lucide-react";
import { SectionCard } from "@/components/report/SectionCard";
import { StatusNotice } from "@/components/report/StatusNotice";
import { useLang } from "@/lib/i18n";

const StatusPill = ({ p }) => {
  const { t } = useLang();
  const open = p.is_open;
  const cls = p.is_expired
    ? "bg-destructive/10 text-destructive"
    : open
    ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
    : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {p.status}
    </span>
  );
};

export const PermitsSection = ({ report, lang }) => {
  const { t } = useLang();
  const block = report.permits || { status: "unavailable", items: [], sources: [] };
  const items = block.items || [];

  let body;
  if (block.status !== "ok") {
    body = <StatusNotice status="unavailable" testid="permits-unavailable" />;
  } else if (items.length === 0) {
    body = <StatusNotice status="none" testid="permits-none" />;
  } else {
    body = (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="py-2 pr-3 font-medium">{t("permit.number")}</th>
              <th className="py-2 pr-3 font-medium">{t("permit.type")}</th>
              <th className="hidden py-2 pr-3 font-medium md:table-cell">{t("permit.desc")}</th>
              <th className="py-2 pr-3 font-medium">{t("permit.issued")}</th>
              <th className="py-2 pr-3 font-medium">{t("permit.status")}</th>
              <th className="py-2 font-medium">{t("permit.authority")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <tr key={`${p.permit_number}-${i}`} data-testid="permit-row" className="border-b border-border/60 last:border-0 align-top">
                <td className="py-3 pr-3 font-mono text-xs font-semibold text-foreground">{p.permit_number}</td>
                <td className="py-3 pr-3 text-foreground">{p.permit_type}</td>
                <td className="hidden py-3 pr-3 text-muted-foreground md:table-cell">{p.description}</td>
                <td className="py-3 pr-3 font-mono text-xs text-muted-foreground">{p.issue_date || "—"}</td>
                <td className="py-3 pr-3"><StatusPill p={p} /></td>
                <td className="py-3 font-mono text-[10px] text-muted-foreground">{p.authority}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(block.notes || []).map((n, i) => (
          <p key={i} className="mt-3 text-xs text-amber-600 dark:text-amber-400">⚠ {n}</p>
        ))}
      </div>
    );
  }

  return (
    <SectionCard
      icon={FileText}
      title={t("sec.permits")}
      count={block.status === "ok" ? items.length : undefined}
      source={(block.sources || [])[0]}
      retrievedAt={report.retrieved_at}
      lang={lang}
      testid="section-permits"
    >
      {body}
      {(block.external_portals || []).length > 0 && (
        <div className="mt-4 border-t border-border pt-3">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t("permit.portals")}</p>
          {block.external_portals.map((p, i) => (
            <a
              key={i}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="permit-portal-link"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              {p.label}
            </a>
          ))}
        </div>
      )}
    </SectionCard>
  );
};
